import React, { ReactNode } from 'react';
import { Flashbar, FlashbarProps } from '@cloudscape-design/components';

interface AnnouncementBannerProps {
  message: ReactNode;
  startAt: Date;
  endAt: Date;
  userBusinessUnit?: string;
  targetBusinessUnits?: readonly string[];
  currentDate?: Date;
}

/**
 * Displays a configured announcement while its display window is active.
 * When target business units are specified, it is limited to those users.
 */
export const isAnnouncementVisible = (
  startAt: Date,
  endAt: Date,
  userBusinessUnit: string,
  targetBusinessUnits: readonly string[] | undefined,
  currentDate: Date = new Date(),
): boolean => {
  const isWithinDisplayWindow = currentDate >= startAt && currentDate < endAt;
  const isTargetBusinessUnit =
    !targetBusinessUnits ||
    targetBusinessUnits.some(
      (businessUnit) => businessUnit.toUpperCase() === userBusinessUnit.toUpperCase(),
    );

  return isWithinDisplayWindow && isTargetBusinessUnit;
};

export const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  message,
  startAt,
  endAt,
  userBusinessUnit = '',
  targetBusinessUnits,
  currentDate,
}) => {
  if (
    !isAnnouncementVisible(
      startAt,
      endAt,
      userBusinessUnit,
      targetBusinessUnits,
      currentDate,
    )
  ) {
    return null;
  }

  const items: FlashbarProps.MessageDefinition[] = [
    {
      type: 'warning',
      content: message,
      id: 'announcement-banner',
      ariaRole: 'status',
    },
  ];

  return (
    <div data-testid="announcement-banner">
      <Flashbar items={items} />
    </div>
  );
};
